/**
 * «Новини» (/info) — РЕДИЗАЙН (VITE_REDESIGN).
 *
 * Прод без прапорця бачить незмінений InfoAnalyticsPageLegacy у page.tsx:
 * маршрут не гейтований жодним VITE_FEATURE_*, тож у прод-збірці сторінка є.
 *
 * Що виправлено проти старої версії:
 *  · власна палітра (#1A1A1A / #121212 / #E6E6E8 / #7C7C86 / var(--accent)) — на токени;
 *  · DAY_COLORS ['#94A3B8','#7FB2E6','#E0A030'] були літералами й не реагували
 *    на зміну теми; тепер брендова рампа через getComputedStyle (§8, §2):
 *    позавчора --series-4, вчора --series-2, сьогодні --series-3 (акцент);
 *  · акценти категорій #6366F1 / #0D7A3C — обидва на --text-muted:
 *    --series-3 теж червонястий (#A6575B/#C56467), а тема новин не
 *    помилка (§3);
 *  · картка «доба» мала власний бордер усередині картки-секції — «плитка на
 *    плитці» (§3); тепер доби розділені вертикальними лініями, а «сьогодні»
 *    позначено акцентом, не рамкою;
 *  · 5 Remix-іконок → Lucide, числа з font-extrabold/tabular-nums → font-display
 *    звичайної насиченості (§0, §5);
 *  · заголовок у тілі прибрано — назву розділу показує топбар (breadcrumb);
 *  · loading більше не ховає всю сторінку через один запит: блоки з'являються
 *    у міру готовності своїх даних.
 *
 * Запити, добір TOP-5, дедуплікація, зведення погодинних даних і всі тексти
 * не змінені.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { pageMaxClass } from '@/utils/pageWidth';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { API_BASE_URL, authFetch } from '@/services/api';
import RdnHourlyChart from '@/pages/market/prices/RdnHourlyChart';
import { Flame, Newspaper, Scale, type LucideIcon } from 'lucide-react';

type CatKey = 'legal' | 'media';
interface Item { source: string; title: string; link: string | null; publishedAt: string | null; fetchedAt: string; imageUrl: string | null; description: string | null; }

const CATS: Record<CatKey, { label: string; Icon: LucideIcon; accent: string }> = {
  legal: { label: 'Законодавство та регулювання', Icon: Scale, accent: 'var(--text-muted)' },
  media: { label: 'Новини ринку та медіа', Icon: Newspaper, accent: 'var(--text-muted)' },
};

const fmt = (n: any) => (n == null ? '—' : Number(n).toLocaleString('uk-UA', { maximumFractionDigits: 0 }));
function ago(iso: string | null): string {
  if (!iso) return ''; const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0 || isNaN(ms)) return ''; const m = Math.floor(ms / 60000);
  if (m < 1) return 'щойно'; if (m < 60) return `${m} хв`;
  const h = Math.floor(m / 60); return h < 24 ? `${h} год` : `${Math.floor(h / 24)} дн`;
}

/**
 * Обмежує список рівно N видимими записами, решту лишає на прокрутку
 * всередині картки.
 *
 * Фіксованою висотою в пікселях цього не зробити: рядок новини може бути в
 * один заголовок, а може мати два рядки заголовка плюс опис, тож той самий
 * ліміт показував би то чотири записи, то шість. Тому висоту міряємо по
 * факту — від верху списку до низу N-го рядка — і перераховуємо, коли
 * змінюється ширина (переноси рядків інші) або сам список.
 */
function useVisibleRows(count: number, itemCount: number) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [maxH, setMaxH] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measure = () => {
      const kids = Array.from(el.children) as HTMLElement[];
      // Записів менше за ліміт — обмежувати нічого, хай картка стискається.
      if (kids.length <= count) { setMaxH(undefined); return; }
      const nth = kids[count - 1];
      const top = el.getBoundingClientRect().top;
      setMaxH(Math.round(nth.getBoundingClientRect().bottom - top));
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [count, itemCount]);

  return { ref, maxH };
}

function TextRow({ it, accent, first }: { it: Item; accent: string; first: boolean }) {
  const showDesc = it.description && it.description.length > 20 && it.description !== it.title
    && !it.title.startsWith(it.description.slice(0, 30));
  return (
    <a href={it.link || '#'} target="_blank" rel="noopener noreferrer" className="block group" style={{ textDecoration: 'none' }}>
      <div className="flex items-start gap-2.5 py-2.5" style={{ borderTop: first ? 'none' : '1px solid var(--line)' }}>
        <span className="w-1.5 h-1.5 rounded-full shrink-0 mt-1.5" style={{ background: accent }} />
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-medium leading-snug group-hover:underline" style={{ color: 'var(--text)' }}>{it.title}</div>
          {showDesc && <div className="text-[11px] mt-0.5 leading-snug line-clamp-2" style={{ color: 'var(--text-muted)' }}>{it.description}</div>}
          <div className="text-[10px] mt-1" style={{ color: 'var(--text-dim)' }}>{it.source} · {ago(it.publishedAt || it.fetchedAt)} тому</div>
        </div>
      </div>
    </a>
  );
}

/** Скільки записів видно в картці теми до появи прокрутки. */
const VISIBLE_NEWS_ROWS = 5;

function CategoryCard({ meta, list }: { meta: { label: string; Icon: LucideIcon; accent: string }; list: Item[] }) {
  const { ref, maxH } = useVisibleRows(VISIBLE_NEWS_ROWS, list.length);
  return (
    <div className="tile p-4">
      <div className="flex items-center gap-2 mb-2">
        <meta.Icon size={15} strokeWidth={1.9} style={{ color: meta.accent }} />
        <h3 className="font-display text-[13.5px] font-semibold" style={{ color: 'var(--text)' }}>{meta.label}</h3>
        {list.length ? (
          <span className="text-[10px] px-1.5 py-0.5"
            style={{ borderRadius: 999, background: 'var(--surface-2)', color: 'var(--text-muted)' }}>{list.length}</span>
        ) : null}
      </div>
      {list.length === 0
        ? <div className="text-[12px] py-4 text-center" style={{ color: 'var(--text-muted)' }}>Немає свіжих подій</div>
        : (
          <div
            ref={ref}
            className="scrollbar-thin"
            style={{ maxHeight: maxH, overflowY: maxH ? 'auto' : 'visible', overscrollBehavior: 'contain' }}
          >
            {list.map((it, j) => <TextRow key={it.link || it.title || j} it={it} accent={meta.accent} first={j === 0} />)}
          </div>
        )}
    </div>
  );
}

/**
 * Вміст розділу «Новини» без обгортки DashboardLayout — щоб його можна було
 * не лише відкрити окремою сторінкою (/info), а й підмонтувати всередину
 * іншої сторінки, яка вже має свій layout (головна ArtemK, де «Новини»
 * прибрані з меню й живуть під основним контентом).
 *
 * `embedded` прибирає повноекранні прийоми сторінки: негативні поля, що
 * виводять фон на всю ширину, власний фон і min-h-screen. Усередині чужого
 * контенту вони б ламали відступи.
 */
export function InfoRedesignContent({ embedded = false }: { embedded?: boolean }) {
  const [items, setItems] = useState<Record<string, Item[]>>({});
  const [date, setDate] = useState('');

  // Запит /info/market-3day прибрано разом із блоком індексів: погодинні ціни
  // РДН тепер тягне RdnHourlyChart сам, зі своїх ендпоїнтів.
  useEffect(() => {
    authFetch(`${API_BASE_URL}/info/digest/briefs`).then((r) => r.json()).then((d) => setDate(d.date || '')).catch(() => {});
    (['legal', 'media'] as const).forEach((cat) =>
      authFetch(`${API_BASE_URL}/info/digest/items?category=${cat}&limit=16&days=14`).then((r) => r.json())
        .then((d) => setItems((p) => ({ ...p, [cat]: d.items || [] }))).catch(() => {}));
  }, []);

  const dateLabel = useMemo(() => { if (!date) return ''; const [y, m, d] = date.split('-'); return `${d}.${m}.${y}`; }, [date]);

  // TOP-5 (картки з зображеннями) з обох тем; решта → текстові рядки за темою.
  const top5 = useMemo(() => {
    const all = [...(items.legal || []).map((x) => ({ ...x, cat: 'legal' as CatKey })),
                 ...(items.media || []).map((x) => ({ ...x, cat: 'media' as CatKey }))]
      .filter((x) => x.imageUrl)
      .sort((a, b) => new Date(b.publishedAt || b.fetchedAt).getTime() - new Date(a.publishedAt || a.fetchedAt).getTime());
    return all.slice(0, 5);
  }, [items]);
  const topSet = useMemo(() => new Set(top5.map((x) => x.link || x.title)), [top5]);
  const rest = (cat: CatKey) => (items[cat] || []).filter((x) => !topSet.has(x.link || x.title));

  const TopCard = ({ it }: { it: Item & { cat: CatKey } }) => {
    const meta = CATS[it.cat];
    return (
      <a href={it.link || '#'} target="_blank" rel="noopener noreferrer"
        className="tile block group overflow-hidden" style={{ textDecoration: 'none' }}>
        <div className="relative w-full" style={{ height: 110, background: 'var(--surface-2)' }}>
          <div className="absolute inset-0 flex items-center justify-center" style={{ color: 'var(--text-dim)' }}>
            <meta.Icon size={24} strokeWidth={1.7} />
          </div>
          {it.imageUrl && (
            <img src={it.imageUrl} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover"
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
          )}
          {/* Затемнення під підписом джерела — це напис поверх фото, а не
              елемент інтерфейсу, тож лишається літеральним. */}
          <span className="absolute top-1.5 left-1.5 text-[9px] font-bold px-1.5 py-0.5"
            style={{ borderRadius: 6, background: 'rgba(0,0,0,0.55)', color: '#fff' }}>
            {it.source.split(' ')[0]}
          </span>
        </div>
        <div className="p-2.5">
          <div className="text-[12px] font-semibold leading-snug line-clamp-3 group-hover:underline" style={{ color: 'var(--text)' }}>
            {it.title}
          </div>
          <div className="text-[10px] mt-1.5" style={{ color: 'var(--text-muted)' }}>
            {ago(it.publishedAt || it.fetchedAt)} тому
          </div>
        </div>
      </a>
    );
  };

  return (
    <div
      className={embedded ? '' : 'min-h-screen -m-4 md:-m-6'}
      style={embedded ? undefined : { background: 'var(--bg)' }}
    >
      <div
        className={embedded ? pageMaxClass(1440) : `px-4 md:px-6 pt-5 pb-10 ${pageMaxClass(1440)}`}
        style={{ fontFamily: 'Urbanist, Inter, sans-serif' }}
      >

          {/* Заголовок у тілі не дублюємо — назву розділу показує топбар.
              Лишається рядок контексту: він несе джерела й дату дайджесту. */}
          <div className="text-[12px] mb-4 flex items-center gap-1.5" style={{ color: 'var(--text-muted)' }}>
            <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: 'var(--ok)' }} />
            Енергоринок України · законодавство · медіа{dateLabel ? ` · ${dateLabel}` : ''}
          </div>

          {/* ── Погодинні ціни РДН ──────────────────────────────────────────
              Замінили блок «Індекси РДН за добу · 3 доби»: той самий графік,
              що й у розділі «Ціни», показує ціну й обсяг по кожній годині —
              це конкретніше за три усереднені добові індекси.

              На «Головній» (embedded) графік НЕ показуємо: там блок новин іде
              одразу під дашбордом, у якого вже є свій ринковий блок, і другий
              графік РДН на одному екрані був би повтором. На окремій сторінці
              «Новини» повтору немає — там він лишається. */}
          {!embedded && (
            <div className="mb-3">
              <RdnHourlyChart height={300} />
            </div>
          )}

          {/* ── Головні події ───────────────────────────────────────────── */}
          {top5.length > 0 && (
            <div className="mb-3">
              <div className="flex items-center gap-2 mb-3">
                <Flame size={15} strokeWidth={1.9} style={{ color: 'var(--text-muted)' }} />
                <h3 className="font-display text-[13.5px] font-semibold" style={{ color: 'var(--text)' }}>Головні події</h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {top5.map((it, j) => <TopCard key={j} it={it} />)}
              </div>
            </div>
          )}

          {/* ── Заголовки за темами ─────────────────────────────────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {(['legal', 'media'] as CatKey[]).map((cat) => (
              <CategoryCard key={cat} meta={CATS[cat]} list={rest(cat)} />
            ))}
          </div>
        </div>
      </div>
  );
}

export default function InfoRedesign() {
  return (
    <DashboardLayout breadcrumb={['Новини']}>
      <InfoRedesignContent />
    </DashboardLayout>
  );
}
